import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectItem } from '@/components/ui/select';
import { ThemedText } from '@/components/themed-text';
import { Expense } from '@/types/expense';
import { EXPENSE_CATEGORY, EXPENSE_CATEGORY_LABELS, type ExpenseCategory } from '@/types/expense';
import {
  useCreateExpense,
  useUpdateExpense,
  type CreateExpensePayload,
} from '@/hooks/use-expenses';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import { format, isValid, parse } from 'date-fns';

type ExpenseModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  expense?: Expense | null;
  onSuccess?: () => void;
};

const CATEGORY_OPTIONS: ExpenseCategory[] = [
  EXPENSE_CATEGORY.MATERIAL_EQUIPMENT,
  EXPENSE_CATEGORY.TRANSPORT,
  EXPENSE_CATEGORY.MARKETING,
  EXPENSE_CATEGORY.TAXES,
  EXPENSE_CATEGORY.OTHER,
];

export function ExpenseModal({ open, onOpenChange, mode, expense, onSuccess }: ExpenseModalProps) {
  const { addToast } = useToastStore();
  const createMutation = useCreateExpense();
  const updateMutation = useUpdateExpense();

  const [amount, setAmount] = useState(expense?.amount ? String(expense.amount) : '');
  const [dateStr, setDateStr] = useState(
    expense?.date ? format(new Date(expense.date), 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy'),
  );
  const [category, setCategory] = useState<ExpenseCategory | ''>(expense?.category || '');
  const [description, setDescription] = useState(expense?.description || '');

  useEffect(() => {
    if (open) {
      setAmount(expense?.amount ? String(expense.amount) : '');
      setDateStr(
        expense?.date
          ? format(new Date(expense.date), 'dd/MM/yyyy')
          : format(new Date(), 'dd/MM/yyyy'),
      );
      setCategory(expense?.category || '');
      setDescription(expense?.description || '');
    }
  }, [open, expense]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  const parseDateInput = (value: string) => {
    const parsed = parse(value, 'dd/MM/yyyy', new Date());
    return isValid(parsed) ? parsed : null;
  };

  const handleSubmit = async () => {
    const amountNum = Number(amount?.replace(',', '.'));
    if (!amount || Number.isNaN(amountNum) || amountNum <= 0) {
      addToast('Informe um valor válido.', 'error');
      return;
    }
    const parsedDate = parseDateInput(dateStr);
    if (!parsedDate) {
      addToast('Data inválida. Use DD/MM/AAAA.', 'error');
      return;
    }

    const payload: CreateExpensePayload = {
      amount: amountNum,
      date: format(parsedDate, 'yyyy-MM-dd'),
      description: description?.trim() || undefined,
    };
    if (category) {
      payload.category = category;
    }

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync(payload);
        addToast('Gasto cadastrado.', 'success');
      } else if (expense?.id) {
        await updateMutation.mutateAsync({
          id: expense.id,
          payload: {
            amount: payload.amount,
            date: payload.date,
            category: payload.category,
            description: payload.description,
          },
        });
        addToast('Gasto atualizado.', 'success');
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      addToast(getApiErrorMessage(error, 'Não foi possível salvar o gasto.'), 'error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !isPending && onOpenChange(value)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            <ThemedText className="font-bold text-xl">
              {mode === 'create' ? 'Novo gasto' : 'Editar gasto'}
            </ThemedText>
          </DialogTitle>
          <DialogDescription>
            <ThemedText className="text-foreground/60 text-sm">
              Registre valor, data e opcionalmente categoria e descrição.
            </ThemedText>
          </DialogDescription>
        </DialogHeader>

        <View className="mt-4 gap-4">
          <View className="gap-2">
            <ThemedText className="font-medium text-sm">Valor (R$)</ThemedText>
            <Input
              placeholder="Ex: 150,00"
              value={amount}
              onChangeText={setAmount}
              keyboardType="decimal-pad"
            />
          </View>

          <View className="gap-2">
            <ThemedText className="font-medium text-sm">Data</ThemedText>
            <Input
              placeholder="DD/MM/AAAA"
              value={dateStr}
              onChangeText={setDateStr}
              keyboardType="numbers-and-punctuation"
            />
          </View>

          <View className="gap-2">
            <ThemedText className="font-medium text-sm">Categoria (opcional)</ThemedText>
            <Select
              value={category}
              onValueChange={(value) => setCategory(value as ExpenseCategory)}
              placeholder="Selecione"
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  <ThemedText>{EXPENSE_CATEGORY_LABELS[opt]}</ThemedText>
                </SelectItem>
              ))}
            </Select>
          </View>

          <View className="gap-2">
            <ThemedText className="font-medium text-sm">Descrição (opcional)</ThemedText>
            <Textarea
              placeholder="Ex: Material de escritório"
              value={description}
              onChangeText={setDescription}
              numberOfLines={3}
              className="min-h-[80px]"
            />
          </View>
        </View>

        <DialogFooter className="mt-6 flex-row items-center justify-end gap-4">
          <Button variant="outline" onPress={() => onOpenChange(false)} disabled={isPending}>
            <ThemedText>Cancelar</ThemedText>
          </Button>
          <Button onPress={handleSubmit} disabled={isPending}>
            <ThemedText className="text-white">
              {mode === 'create' ? 'Cadastrar' : 'Salvar'}
            </ThemedText>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
